import { useMemo } from 'react';
import { useQuery, useQueries } from '@tanstack/react-query';
import { api } from '../api/client';
import { useAppStore } from '../store';
import type { TimelineBlock } from '../types';

export function usePlannerBlocks(weekDates: string[]) {
  const startDate = weekDates[0];
  const endDate = weekDates[6];

  const { data } = useQuery<TimelineBlock[]>({
    queryKey: ['timeline-blocks', 'range', startDate, endDate],
    queryFn: () => api.timelineBlocks.listRange(startDate, endDate),
    enabled: !!startDate && !!endDate,
  });

  // Group blocks by date
  const blocksByDate = useMemo(() => {
    const map = new Map<string, TimelineBlock[]>();
    for (const date of weekDates) map.set(date, []);
    for (const block of (data ?? [])) {
      const list = map.get(block.date) ?? [];
      list.push(block);
      map.set(block.date, list);
    }
    return map;
  }, [data, weekDates]);

  return blocksByDate;
}

export function usePlannerEvents(weekDates: string[]) {
  const spaceId = useAppStore((s) => s.currentSpaceId);

  const queries = useQueries({
    queries: weekDates.map((date) => ({
      queryKey: ['calendar-events', date, spaceId],
      queryFn: () => api.calendarEvents.list(date),
      enabled: !!date,
    })),
  });

  // Group events by date
  const eventsByDate = useMemo(() => {
    const map = new Map<string, any[]>();
    weekDates.forEach((date, i) => {
      map.set(date, queries[i]?.data ?? []);
    });
    return map;
  }, [queries, weekDates]);

  return eventsByDate;
}
